import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  type Ref,
} from "react";
import { useCallback } from "react";
import { Toast } from "primereact/toast";
import { OrderLocation } from "./InfoPopup";
import { summarizePolygonOrders, type PolygonSelectionSummary } from "./polygon-selection";

import {
  getOptimizedRoutesForDrivers,
  OrderLocationWithSeq,
  OptimizedDriverRoute,
} from "./getOptimizedRoute";
import {
  seqVal,
  clusterByDistance,
  buildMarkerDefsContiguous,
  drawChainedDirectionsAsPolyline,
  routeOnce,
  directionsTotals,
  MarkerDef,
  DEFAULT_CLUSTER_TOLERANCE_M,
} from "./MapRouteUtils";
import BigDrawingControls from "./BigDrawingControls";
import type { RouteForExport } from "./ExportRoutesExcel";
import { parseRoutePoint, type DriverAssignment } from "./route-preview";
import type { RouteReviewContext } from "./RoutePreviewDialog";
import { afterMapPaint } from "./map-paint";
import { createRoot, type Root } from "react-dom/client";
import { Tag } from "primereact/tag";
import { makeStopIcon, makeDepotIcon, routeColor } from "./route-map-style";
import { attachRouteOrderPopup } from "./route-order-popup";

export type PolygonManagerHandle = {
  clearRoutes: () => void;
  assignSelection: () => Promise<void>;
  addOrderToRoute?: (orderId: string, routeIndex?: number) => Promise<void>;
  /** Reverse the sequence & redraw the path for a given driver */
  reverseDriverRoute: (driverId: number) => Promise<void>;
};

type PolygonRoute = {
  popupCleanups?: Array<() => void>;
  polygon: google.maps.Polygon;
  driverId: number;
  driverName: string;
  color: string;
  phonenumber?: string;
  start: google.maps.LatLngLiteral;
  end: google.maps.LatLngLiteral;
  directionsRenderers?: google.maps.DirectionsRenderer[];
  polyline?: google.maps.Polyline;
  markers: google.maps.Marker[]; // includes start/end + stop markers
  ordersInRoute: OrderLocationWithSeq[]; // must include lat,lng, orderId, sequence
  totalDistanceKm?: number; // from optimization response
  drivingMinutes?: number;
  totalTaxableAmount?: number;
  vehicleNo: string;                  // 🔥 Added
};

interface PolygonManagerProps {
  map: google.maps.Map;
  orders: OrderLocation[];
  onOrdersRouted?: (orderIds: (string | number)[]) => void;
  onOrdersUnrouted?: (orderIds: (string | number)[]) => void;
  onClearRouted?: () => void;
  onRoutesForExportChange?: (routes: RouteForExport[]) => void;
  onOptimizedDriversChange?: (
    drivers: { driverId: number; driverName: string; orderCount: number; totalDistanceKm: number; totalTaxableAmount: number; }[]
  ) => void;
  totalOrderCount: number;
  onSelectionChange?: (summary: PolygonSelectionSummary | null) => void;
  onSelectionBusyChange?: (busy: boolean) => void;
  routeAssignments?: DriverAssignment[];
  reviewRoutes?: (context: RouteReviewContext) => Promise<OptimizedDriverRoute[] | null>;
  drawingEnabled?: boolean;
  autoReview?: boolean;
  drawingControlHost?: HTMLElement | null;
  gpsControlHost?: HTMLElement | null;
}

function toExport(routes: PolygonRoute[]): RouteForExport[] {
  return routes.map((r) => ({
    driverId: r.driverId,
    totalDistanceKm: r.totalDistanceKm,
    drivingMinutes: r.drivingMinutes,
    driverName: r.driverName,
    phonenumber: r.phonenumber,
    vehicleNo: r.vehicleNo ?? "",     // 🔥 Added
    ordersInRoute: r.ordersInRoute
      .slice()
      .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))
      .map((o) => ({
        orderId: (o as any).orderId,
        sequence: (o as any).sequence as number | undefined,
      })),
  }));
}

function renumber<T extends { sequence?: number }>(list: T[]): T[] {
  return list.map((o, i) => ({ ...o, sequence: i + 1 }));
}

const selectionSignature = (selected: OrderLocation[]) => JSON.stringify(selected.map(o =>
  [o.orderId, o.lat, o.lng, o.customerId, o.addressId, o.taxableAmount ?? o.amount]).sort());

const REVIEW_RESHAPE_DELAY_MS = 600;

const PolygonManager = forwardRef<PolygonManagerHandle, PolygonManagerProps>(
  function PolygonManager(
    {
      map,
      orders,
      onOrdersRouted,
      onOrdersUnrouted,
      onClearRouted,
      onRoutesForExportChange,
      onOptimizedDriversChange,
      totalOrderCount,
      onSelectionChange,
      onSelectionBusyChange,
      routeAssignments = [], reviewRoutes, drawingEnabled = false, autoReview = false, drawingControlHost, gpsControlHost,
    },
    ref: Ref<PolygonManagerHandle>
  ) {
    const toastRef = useRef<Toast>(null);
    const pendingPolygonRef = useRef<google.maps.Polygon | null>(null);
    const manuallyIncluded = useRef(new Set<string>());
    const selectionListenersRef = useRef<google.maps.MapsEventListener[]>([]);
    const selectionWorkingRef = useRef(false);
    const routeGenerationRef = useRef(0);
    const routeSetupRef = useRef({ routeAssignments, reviewRoutes, drawingEnabled, autoReview });
    routeSetupRef.current = { routeAssignments, reviewRoutes, drawingEnabled, autoReview };
    const assignSelectionRef = useRef<() => Promise<void>>(async () => {});
    // Reshaping the boundary while the route review is open rebuilds the review with the newly covered orders.
    const reviewActiveRef = useRef(false);
    const reviewSignatureRef = useRef("");
    const cancelReviewRef = useRef<(() => void) | null>(null);
    const reshapeRequestedRef = useRef(false);
    const reshapeTimerRef = useRef<number | undefined>(undefined);

    // drawing manager refs
    const dmRef = useRef<google.maps.drawing.DrawingManager | null>(null);
    const [drawingManager, setDrawingManager] =
      useState<google.maps.drawing.DrawingManager | null>(null);
    const overlayListenerRef = useRef<google.maps.MapsEventListener | null>(null);

    // gps pill
    const gpsHostRef = useRef<HTMLDivElement | null>(null);
    const gpsRootRef = useRef<Root | null>(null);

    useEffect(() => {
      if (!map) return;
      map.setOptions({
        fullscreenControl: true,
        fullscreenControlOptions: { position: google.maps.ControlPosition.TOP_RIGHT },
      });
    }, [map]);

    useEffect(() => {
      if (!map) return;
      const host = document.createElement("div");
      if (!gpsControlHost) Object.assign(host.style, {
        position: "absolute",
        top: `8px`,
        right: `60px`,
        zIndex: "5",
        display: "flex",
        alignItems: "center",
        pointerEvents: "auto",
      } as CSSStyleDeclaration);
      const mapDiv = map.getDiv() as HTMLElement;
      if (getComputedStyle(mapDiv).position === "static") mapDiv.style.position = "relative";
      (gpsControlHost || mapDiv).appendChild(host);
      gpsHostRef.current = host;
      gpsRootRef.current = createRoot(host);
      return () => {
        gpsRootRef.current?.unmount();
        gpsRootRef.current = null;
        host.remove();
        gpsHostRef.current = null;
      };
    }, [map, gpsControlHost]);

    function renderGpsPill(visible: number, total: number) {
      if (!gpsRootRef.current) return;
      gpsRootRef.current.render(
        <Tag
          icon="pi pi-check"
          value={`GPS: ${visible}/${total}`}
          style={{
            padding: "0.6rem 0.9rem",
            fontSize: "0.95rem",
            backgroundColor: "#f0f5f3",
            color: "#166534",
            border: "1px solid #4ade80",
            borderRadius: "20px",
            whiteSpace: "nowrap",
          }}
        />
      );
    }
    useEffect(() => {
      renderGpsPill(orders.length, totalOrderCount);
    }, [orders.length, totalOrderCount, gpsControlHost]);

    // core routing state
    const ordersRef = useRef<OrderLocation[]>(orders);
    useEffect(() => void (ordersRef.current = orders), [orders]);

    const [polygonRoutes, setPolygonRoutes] = useState<PolygonRoute[]>([]);
    const polygonRoutesRef = useRef<PolygonRoute[]>([]);
    useEffect(() => void (polygonRoutesRef.current = polygonRoutes), [polygonRoutes]);

    const eligibleOrders = useCallback((polygon: google.maps.Polygon) => {
      const routed = new Set(polygonRoutesRef.current.flatMap(r => r.ordersInRoute.map(o => String(o.orderId))));
      const eligible = ordersRef.current.filter(o => !o.isAssigned && !routed.has(String(o.orderId)) &&
        Number.isFinite(o.lat) && Number.isFinite(o.lng) &&
        (manuallyIncluded.current.has(String(o.orderId)) || google.maps.geometry.poly.containsLocation(new google.maps.LatLng(o.lat, o.lng), polygon)));
      return Array.from(new Map(eligible.map(o => [String(o.orderId), o])).values());
    }, []);

    const clearPendingSelection = useCallback((removePolygon = true) => {
      window.clearTimeout(reshapeTimerRef.current);
      manuallyIncluded.current.clear();
      selectionListenersRef.current.forEach(listener => google.maps.event.removeListener(listener));
      selectionListenersRef.current = [];
      if (removePolygon) pendingPolygonRef.current?.setMap(null);
      pendingPolygonRef.current = null;
      onSelectionChange?.(null);
    }, [onSelectionChange]);

    const publishSelection = useCallback(() => {
      if (pendingPolygonRef.current)
        onSelectionChange?.(summarizePolygonOrders(eligibleOrders(pendingPolygonRef.current)));
    }, [eligibleOrders, onSelectionChange]);

    useEffect(() => { publishSelection(); }, [orders, publishSelection]);

    useEffect(() => () => {
      window.clearTimeout(reshapeTimerRef.current);
      routeGenerationRef.current++;
      selectionListenersRef.current.forEach(listener => google.maps.event.removeListener(listener));
      pendingPolygonRef.current?.setMap(null);
    }, []);

    const publishDrivers = useCallback(() => {
      onOptimizedDriversChange?.(
        polygonRoutesRef.current.map((r) => ({
          driverId: r.driverId,
          driverName: r.driverName,
          orderCount: r.ordersInRoute.length,
          totalDistanceKm: r.totalDistanceKm ?? 0,
          totalTaxableAmount: r.totalTaxableAmount ?? 0,
        }))
      );
    }, [onOptimizedDriversChange]);

    // store markers (unchanged)
    useEffect(() => {
      const STORE_LOCATIONS = [
        { lat: 12.9593743, lng: 77.5168123 },
        { lat: 12.437657, lng: 76.3427597 },
        { lat: 12.2724, lng: 76.6711 },
        { lat: 12.982306, lng: 77.445667 },
      ];
      const storeMarkers: google.maps.Marker[] = [];
      function storeIcon(): google.maps.Icon {
        return {
          url:
            "data:image/svg+xml;utf-8," +
            encodeURIComponent(`
              <svg width="36" height="36" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="11" fill="#FFA726" stroke="#333" stroke-width="1.5"/>
                <path d="M7 9h10l-1.5 5h-7z" fill="#fff" stroke="#333" stroke-width="1.5"/>
                <circle cx="10" cy="16" r="1" fill="#333"/>
                <circle cx="14" cy="16" r="1" fill="#333"/>
              </svg>
            `),
          scaledSize: new google.maps.Size(36, 36),
          anchor: new google.maps.Point(18, 33),
        };
      }
      STORE_LOCATIONS.forEach((store) =>
        storeMarkers.push(new google.maps.Marker({ map, position: store, icon: storeIcon(), zIndex: 2000 }))
      );
      return () => storeMarkers.forEach((m) => m.setMap(null));
    }, [map]);

    // Helper to destroy map artifacts for a route entry
    const teardownDrawn = useCallback((r: PolygonRoute) => {
      r.popupCleanups?.forEach(close => close()); r.popupCleanups = [];
      r.directionsRenderers?.forEach((dr) => dr.setMap(null));
      r.polyline?.setMap(null);
      r.markers.forEach((m) => m.setMap(null));
      r.directionsRenderers = undefined;
      r.polyline = undefined;
      r.markers = [];
    }, []);
    useEffect(() => () => { polygonRoutesRef.current.forEach(teardownDrawn); }, [teardownDrawn]);

    // The single place where polygon selection gets processed
    const processPolygonSelection = async (inside: OrderLocation[], drawnPolygon: google.maps.Polygon, isCurrent: () => boolean,
      replacement?: { route: PolygonRoute; assignments: DriverAssignment[] }, onReviewActive?: (active: boolean) => void) => {
      const setup = routeSetupRef.current;
      if (!setup.reviewRoutes || !setup.routeAssignments.length) return false;
      const baseReview = replacement ? (context: RouteReviewContext) => setup.reviewRoutes!({ ...context,
        replacingRouteIndex: polygonRoutesRef.current.indexOf(replacement.route) }) : setup.reviewRoutes;
      // The review promise can be cancelled from here so a reshaped boundary can open a fresh review.
      const review = onReviewActive ? (context: RouteReviewContext) => new Promise<OptimizedDriverRoute[] | null>((resolve, reject) => {
        let settled = false;
        const finish = () => { settled = true; cancelReviewRef.current = null; onReviewActive(false); };
        onReviewActive(true);
        cancelReviewRef.current = () => { if (!settled) { finish(); resolve(null); } };
        baseReview(context).then(routes => { if (!settled) { finish(); resolve(routes); } }, error => { if (!settled) { finish(); reject(error); } });
      }) : baseReview;
      const driverRoutes: OptimizedDriverRoute[] | null = await getOptimizedRoutesForDrivers(inside, replacement?.assignments ?? setup.routeAssignments, review);
      if (!isCurrent()) return false;
      if (!driverRoutes || driverRoutes.length === 0) {
        return false; // Cancelling the driver picker leaves the boundary available for retry.
      }

      const newEntries: PolygonRoute[] = [];

      for (let i = 0; i < driverRoutes.length; i++) {
        const dr = driverRoutes[i];
        const { driverId, driverName, stops, start, end, phonenumber } = dr;
        if (!stops || stops.length < 1) continue;

        const color = routeColor(dr.color);
        const sortedRoute = [...stops].sort((a, b) => seqVal(a) - seqVal(b));

        const depotStart = start ?? { lat: sortedRoute[0].lat, lng: sortedRoute[0].lng };
        const depotEnd =
          end ?? { lat: sortedRoute[sortedRoute.length - 1].lat, lng: sortedRoute[sortedRoute.length - 1].lng };

        const clusters = clusterByDistance(sortedRoute, DEFAULT_CLUSTER_TOLERANCE_M);
        const markerDefs: MarkerDef[] = buildMarkerDefsContiguous(clusters);

        const totalTaxableAmount = sortedRoute.reduce(
          (sum, s) => sum + Number((s as any).taxableAmount ?? 0),
          0
        );

        const routeTotalKm =
          Number((dr as any).totalDistanceKm ?? 0) ||
          stops.reduce((s, x) => s + Number(x.totalDistanceKm ?? 0), 0);

        // create markers for stops (and depots later)
        const markers: google.maps.Marker[] = markerDefs.map((g) => {
          return new google.maps.Marker({
            position: g.pos,
            icon: makeStopIcon(color),
            label: { text: g.label, color: "white", fontWeight: "bold" },
            title: `${driverName} • ${g.title}`,
            zIndex: 100,
            map,
          });
        });

        const startMarker = new google.maps.Marker({
          position: depotStart,
          icon: makeDepotIcon("start", color),
          label: { text: "S", color: "white", fontWeight: "bold" },
          title: `${driverName} • Start${phonenumber ? " • " + phonenumber : ""}`,
          zIndex: 1000,
          map,
        });

        const endMarker = new google.maps.Marker({
          position: depotEnd,
          icon: makeDepotIcon("end", color),
          label: { text: "E", color: "white", fontWeight: "bold" },
          title: `${driverName} • End${phonenumber ? " • " + phonenumber : ""}`,
          zIndex: 1000,
          map,
        });

        markers.push(startMarker, endMarker);

        try {
          const stopCoords = sortedRoute.map((s) => ({ lat: s.lat, lng: s.lng }));
          const origin = depotStart;
          const destination = depotEnd;

          const same = (a: google.maps.LatLngLiteral, b: google.maps.LatLngLiteral) =>
            Math.abs(a.lat - b.lat) < 1e-6 && Math.abs(a.lng - b.lng) < 1e-6;

          const prunedStops = stopCoords.filter((c, idx, arr) => {
            if (idx === 0 && same(c, origin)) return false;
            if (idx === arr.length - 1 && same(c, destination)) return false;
            return true;
          });

          if (prunedStops.length + 2 <= 25) {
            const waypoints = prunedStops.map((c) => ({
              location: new google.maps.LatLng(c.lat, c.lng),
              stopover: true,
            }));

            const directionsRenderer = new google.maps.DirectionsRenderer({
              suppressMarkers: true,
              preserveViewport: true,
              map,
              polylineOptions: { strokeColor: color, strokeOpacity: 0.95, strokeWeight: 5 },
            });

            const ds = new google.maps.DirectionsService();
            const result = await routeOnce(ds, {
              origin: new google.maps.LatLng(origin.lat, origin.lng),
              destination: new google.maps.LatLng(destination.lat, destination.lng),
              waypoints,
              optimizeWaypoints: false,
              travelMode: google.maps.TravelMode.DRIVING,
            });
            const totals = directionsTotals(result);
            directionsRenderer.setDirections(result);

            newEntries.push({
              polygon: drawnPolygon,
              driverId,
              driverName,
              color,
              phonenumber,
              start: depotStart,
              end: depotEnd,
              directionsRenderers: [directionsRenderer],
              markers,
              ordersInRoute: sortedRoute,
              totalDistanceKm: totals?.distanceKm ?? routeTotalKm,
              drivingMinutes: totals?.drivingMinutes ?? dr.drivingMinutes,
              totalTaxableAmount,
              vehicleNo: dr.vehicleNo ?? "",       // 🔥 Added
            });
          } else {
            const fullPath = [origin, ...prunedStops, destination];
            let totals: { distanceKm: number; drivingMinutes: number } | null = null;
            const polyline = await drawChainedDirectionsAsPolyline(map, fullPath, undefined, value => { totals = value; });
            polyline.setOptions({ strokeColor: color, strokeOpacity: 0.95, strokeWeight: 5 });

            newEntries.push({
              polygon: drawnPolygon,
              driverId,
              driverName,
              color,
              phonenumber,
              start: depotStart,
              end: depotEnd,
              polyline,
              markers,
              ordersInRoute: sortedRoute,
              totalDistanceKm: (totals as { distanceKm: number } | null)?.distanceKm ?? routeTotalKm,
              drivingMinutes: (totals as { drivingMinutes: number } | null)?.drivingMinutes ?? dr.drivingMinutes,
              totalTaxableAmount,
              vehicleNo: dr.vehicleNo ?? "",       // 🔥 Added
            });
          }
        } catch (err) {
          console.error(err);
          alert(`Couldn't draw the route for ${driverName}.`);
          markers.forEach((m) => m.setMap(null));
        }
      }

      await afterMapPaint();
      if (!isCurrent()) {
        newEntries.forEach(teardownDrawn);
        return false;
      }
      if (newEntries.length !== driverRoutes.filter(route => route.stops.length).length) {
        newEntries.forEach(teardownDrawn);
        return false;
      }
      if (newEntries.length === 0) return false;

      // Publish routed orders to parent
      const includedIds = new Set<string | number>();
      newEntries.forEach((e) =>
        e.ordersInRoute.forEach((o) => {
          if (o.orderId != null) includedIds.add(o.orderId as any);
        })
      );
      onOrdersRouted?.(Array.from(includedIds));

      // Append to existing routes and publish
      newEntries.forEach(entry => bindRoutePopups(entry));
      if (replacement) {
        teardownDrawn(replacement.route);
        const removedIds = replacement.route.ordersInRoute.map(order => String(order.orderId)).filter(id => !includedIds.has(id));
        if (removedIds.length) onOrdersUnrouted?.(removedIds);
      }
      const next = [...polygonRoutesRef.current.filter(route => route !== replacement?.route), ...newEntries];
      setPolygonRoutes(next);
      polygonRoutesRef.current = next;
      onRoutesForExportChange?.(toExport(next));
      publishDrivers();

      // Add right-click handler for cleaning up this polygon's routes
      newEntries.forEach((entry) => {
        entry.polygon.addListener("rightclick", () => {
          const routesNow = polygonRoutesRef.current;
          const removedIds: (string | number)[] = [];
          const remaining = routesNow.filter((route) => {
            const isThis = route.polygon === entry.polygon;
            if (isThis) {
              route.popupCleanups?.forEach(close => close());
              route.directionsRenderers?.forEach((dr) => dr.setMap(null));
              route.polyline?.setMap(null);
              route.markers.forEach((m) => m.setMap(null));
              route.polygon.setMap(null);
              route.ordersInRoute.forEach((o) => {
                if (o.orderId != null) removedIds.push(o.orderId as any);
              });
            }
            return !isThis;
          });
          if (removedIds.length) onOrdersUnrouted?.(removedIds);
          setPolygonRoutes(remaining);
          polygonRoutesRef.current = remaining;
          onRoutesForExportChange?.(toExport(remaining));
          publishDrivers();
        });
      });
      return true;
    };

    const assignSelection = async () => {
      const polygon = pendingPolygonRef.current;
      if (!polygon || selectionWorkingRef.current || !routeSetupRef.current.drawingEnabled) return;
      publishSelection();
      if (eligibleOrders(polygon).length < 2) return;
      const generation = routeGenerationRef.current;
      selectionWorkingRef.current = true;
      onSelectionBusyChange?.(true);
      // The boundary stays editable while the review is open; it locks once the roads are being drawn.
      const onReviewActive = (active: boolean) => {
        reviewActiveRef.current = active;
        if (active) polygon.setEditable(true);
        else { window.clearTimeout(reshapeTimerRef.current); if (!reshapeRequestedRef.current) polygon.setEditable(false); }
      };
      try {
        for (;;) {
          const inside = eligibleOrders(polygon);
          const original = selectionSignature(inside);
          const isCurrent = () => routeGenerationRef.current === generation && pendingPolygonRef.current === polygon &&
            selectionSignature(eligibleOrders(polygon)) === original;
          reviewSignatureRef.current = original;
          reshapeRequestedRef.current = false;
          const prepared = await processPolygonSelection(inside, polygon, isCurrent, undefined, onReviewActive);
          if (prepared) { clearPendingSelection(false); break; }
          if (reshapeRequestedRef.current && routeGenerationRef.current === generation && pendingPolygonRef.current === polygon) continue;
          if (!isCurrent() && pendingPolygonRef.current === polygon)
            toastRef.current?.show({ severity: "warn", summary: "Selection changed", detail: "Review the updated boundary and rebuild the route." });
          break;
        }
      } catch {
        toastRef.current?.show({ severity: "error", summary: "Route unavailable", detail: "Could not prepare the route. Please retry." });
      } finally {
        reshapeRequestedRef.current = false;
        selectionWorkingRef.current = false;
        onSelectionBusyChange?.(false);
        if (pendingPolygonRef.current === polygon) { polygon.setEditable(true); publishSelection(); }
      }
    };
    assignSelectionRef.current = assignSelection;

    // Dragging the boundary updates the live count; while a review is open it also rebuilds the review.
    const handlePathEdit = useCallback(() => {
      publishSelection();
      if (!reviewActiveRef.current) return;
      window.clearTimeout(reshapeTimerRef.current);
      reshapeTimerRef.current = window.setTimeout(() => {
        const polygon = pendingPolygonRef.current;
        if (!polygon || !reviewActiveRef.current) return;
        const covered = eligibleOrders(polygon);
        if (selectionSignature(covered) === reviewSignatureRef.current) return;
        if (covered.length < 2) {
          toastRef.current?.show({ severity: "warn", summary: "Boundary too small", detail: "Keep at least 2 unassigned orders inside the boundary." });
          return;
        }
        reshapeRequestedRef.current = true;
        cancelReviewRef.current?.();
      }, REVIEW_RESHAPE_DELAY_MS);
    }, [eligibleOrders, publishSelection]);

    // Completing a boundary only publishes a preview; the toolbar starts driver selection.
    useEffect(() => {
      if (!google?.maps?.drawing || !google?.maps?.geometry?.poly) {
        console.error("Google Maps libraries missing. Load with `libraries=drawing,geometry`.");
        return;
      }
      if (!dmRef.current) {
        dmRef.current = new google.maps.drawing.DrawingManager({
          drawingControl: false,
          drawingMode: null,
          polygonOptions: {
            fillColor: "#ff4d4f",
            fillOpacity: 0.2,
            strokeWeight: 2,
            clickable: true,
            editable: true,
            zIndex: 1,
          },
        });
      }
      dmRef.current.setMap(map);
      setDrawingManager(dmRef.current);

      if (!overlayListenerRef.current) {
        overlayListenerRef.current = google.maps.event.addListener(
          dmRef.current,
          "overlaycomplete",
          (event: google.maps.drawing.OverlayCompleteEvent) => {
            const drawnPolygon = event.overlay as google.maps.Polygon;
            dmRef.current!.setOptions({ drawingMode: null });

            if (selectionWorkingRef.current || !routeSetupRef.current.drawingEnabled) {
              drawnPolygon.setMap(null);
              return;
            }
            clearPendingSelection();
            pendingPolygonRef.current = drawnPolygon;
            publishSelection();
            const path = drawnPolygon.getPath();
            selectionListenersRef.current = [
              ...["insert_at", "set_at", "remove_at"].map(name => google.maps.event.addListener(path, name, handlePathEdit)),
              google.maps.event.addListener(drawnPolygon, "rightclick", () => {
                if (!selectionWorkingRef.current) clearPendingSelection();
              }),
            ];
            if (routeSetupRef.current.autoReview) void assignSelectionRef.current();
          }
        );
      }

      return () => {
        try {
          if (overlayListenerRef.current) {
            google.maps.event.removeListener(overlayListenerRef.current);
            overlayListenerRef.current = null;
          }
        } catch {}
      };
    }, [map, clearPendingSelection, publishSelection, handlePathEdit]);

    useEffect(() => {
      if (dmRef.current) {
        dmRef.current.setMap(map);
        setDrawingManager(dmRef.current);
        setTimeout(() => google.maps.event.trigger(map, "resize"), 100);
      }
    }, [map]);

    const clearAllPolygons = () => {
      routeGenerationRef.current++;
      clearPendingSelection();
      const current = polygonRoutesRef.current;
      const allIds: (string | number)[] = [];
      current.forEach(({ polygon, directionsRenderers, polyline, markers, ordersInRoute, popupCleanups }) => {
        popupCleanups?.forEach(close => close());
        polygon.setMap(null);
        directionsRenderers?.forEach((dr) => dr.setMap(null));
        polyline?.setMap(null);
        markers.forEach((m) => m.setMap(null));
        ordersInRoute.forEach((o) => {
          if (o.orderId != null) allIds.push(o.orderId as any);
        });
      });
      setPolygonRoutes([]);
      polygonRoutesRef.current = [];
      if (allIds.length) onOrdersUnrouted?.(allIds);
      onClearRouted?.();
      onRoutesForExportChange?.([]);
      publishDrivers();
    };

    const editAppliedRoute = async (route: PolygonRoute, orderId?: string, end?: "start" | "end" | "remove") => {
      if (selectionWorkingRef.current || !polygonRoutesRef.current.includes(route)) throw new Error("The route is busy.");
      const selected = route.ordersInRoute.find(order => String(order.orderId) === orderId);
      if (orderId && !selected) throw new Error("Choose an order in this route.");
      const ordered = [...route.ordersInRoute].sort((a,b) => (a.sequence ?? 0) - (b.sequence ?? 0));
      const removing = end === "remove";
      if (removing && ordered.length <= 1) throw new Error("Keep at least one order in the route.");
      const remaining = ordered.filter(order => order !== selected);
      const next: PolygonRoute = { ...route, markers: [], directionsRenderers: undefined, polyline: undefined, popupCleanups: [],
        ordersInRoute: renumber(removing ? remaining : selected ? end === "start" ? [selected, ...ordered.filter(order => order !== selected)]
          : [...ordered.filter(order => order !== selected), selected] : ordered.reverse()),
        start: selected ? end === "start" ? { lat: selected.lat, lng: selected.lng } : route.start : route.end,
        end: selected ? end === "end" ? { lat: selected.lat, lng: selected.lng } : route.end : route.start };
      if (removing && selected) {
        const driver = routeSetupRef.current.routeAssignments.find(item => item.driver.driverId === route.driverId)?.driver;
        const same = (a: { lat: number; lng: number }, b: { lat: number; lng: number }) => Math.abs(a.lat - b.lat) < 1e-6 && Math.abs(a.lng - b.lng) < 1e-6;
        for (const endpoint of ["start", "end"] as const) {
          if (same(route[endpoint], selected) && !remaining.some(order => same(order, route[endpoint]))) {
            try { next[endpoint] = parseRoutePoint(endpoint === "start" ? driver?.startLocation : driver?.endLocation); }
            catch { const order = endpoint === "start" ? remaining[0] : remaining[remaining.length - 1]; next[endpoint] = { lat: order.lat, lng: order.lng }; }
          }
        }
        next.totalTaxableAmount = remaining.reduce((sum, order) => sum + Number(order.taxableAmount ?? order.amount ?? 0), 0);
      }
      const generation = routeGenerationRef.current;
      selectionWorkingRef.current = true; onSelectionBusyChange?.(true);
      try {
        await redrawRoute(next); await afterMapPaint();
        if (generation !== routeGenerationRef.current || !polygonRoutesRef.current.includes(route)) throw new Error("The route changed.");
        teardownDrawn(route); bindRoutePopups(next);
        polygonRoutesRef.current = polygonRoutesRef.current.map(item => item === route ? next : item);
        if (removing && orderId) onOrdersUnrouted?.([orderId]);
        setPolygonRoutes([...polygonRoutesRef.current]); onRoutesForExportChange?.(toExport(polygonRoutesRef.current)); publishDrivers();
      } catch (error) { teardownDrawn(next); throw error; }
      finally { selectionWorkingRef.current = false; onSelectionBusyChange?.(false); }
    };
    const bindRoutePopups = (route: PolygonRoute) => {
      const definitions = buildMarkerDefsContiguous(clusterByDistance([...route.ordersInRoute].sort((a,b) => seqVal(a) - seqVal(b)), DEFAULT_CLUSTER_TOLERANCE_M));
      route.popupCleanups = definitions.map((definition, index) => attachRouteOrderPopup(route.markers[index], map,
        route.ordersInRoute.filter(order => definition.orderIds?.includes(String(order.orderId))), {
          onSetEndpoint: (orderId, end) => editAppliedRoute(route, orderId, end), onReverseRoute: () => editAppliedRoute(route),
          onRemoveFromRoute: orderId => editAppliedRoute(route, orderId, "remove"), removeDisabled: route.ordersInRoute.length <= 1,
        }));
      [route.start, route.end].forEach((point, index) => {
        const group = route.ordersInRoute.filter(order => Math.abs(order.lat - point.lat) < 1e-6 && Math.abs(order.lng - point.lng) < 1e-6);
        if (group.length) route.popupCleanups!.push(attachRouteOrderPopup(route.markers[definitions.length + index], map, group, {
          onSetEndpoint: (orderId, end) => editAppliedRoute(route, orderId, end), onReverseRoute: () => editAppliedRoute(route),
          onRemoveFromRoute: orderId => editAppliedRoute(route, orderId, "remove"), removeDisabled: route.ordersInRoute.length <= 1,
        }));
      });
    };

    /** Redraw a route after changing its `ordersInRoute`, `start`, `end` */
    const redrawRoute = async (r: PolygonRoute) => {
      const color = r.color;
      const sortedRoute = r.ordersInRoute.slice().sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));

      const clusters = clusterByDistance(sortedRoute, DEFAULT_CLUSTER_TOLERANCE_M);
      const markerDefs: MarkerDef[] = buildMarkerDefsContiguous(clusters);

      // stop markers
      r.markers = markerDefs.map((g) => {
        return new google.maps.Marker({
          position: g.pos,
          icon: makeStopIcon(color),
          label: { text: g.label, color: "white", fontWeight: "bold" },
          title: `${r.driverName} • ${g.title}`,
          zIndex: 100,
          map,
        });
      });

      // depot markers
      const startMarker = new google.maps.Marker({
        position: r.start,
        icon: makeDepotIcon("start", color),
        label: { text: "S", color: "white", fontWeight: "bold" },
        title: `${r.driverName} • Start${r.phonenumber ? " • " + r.phonenumber : ""}`,
        zIndex: 1000,
        map,
      });
      const endMarker = new google.maps.Marker({
        position: r.end,
        icon: makeDepotIcon("end", color),
        label: { text: "E", color: "white", fontWeight: "bold" },
        title: `${r.driverName} • End${r.phonenumber ? " • " + r.phonenumber : ""}`,
        zIndex: 1000,
        map,
      });
      r.markers.push(startMarker, endMarker);

      // path
      const stopCoords = sortedRoute.map((s) => ({ lat: s.lat, lng: s.lng }));
      const origin = r.start;
      const destination = r.end;

      const same = (a: google.maps.LatLngLiteral, b: google.maps.LatLngLiteral) =>
        Math.abs(a.lat - b.lat) < 1e-6 && Math.abs(a.lng - b.lng) < 1e-6;

      const prunedStops = stopCoords.filter((c, idx, arr) => {
        if (idx === 0 && same(c, origin)) return false;
        if (idx === arr.length - 1 && same(c, destination)) return false;
        return true;
      });

      if (prunedStops.length + 2 <= 25) {
        const waypoints = prunedStops.map((c) => ({
          location: new google.maps.LatLng(c.lat, c.lng),
          stopover: true,
        }));
        const directionsRenderer = new google.maps.DirectionsRenderer({
          suppressMarkers: true,
          preserveViewport: true,
          map,
          polylineOptions: { strokeColor: color, strokeOpacity: 0.95, strokeWeight: 5 },
        });
        r.directionsRenderers = [directionsRenderer];
        const ds = new google.maps.DirectionsService();
        const result = await routeOnce(ds, {
          origin: new google.maps.LatLng(origin.lat, origin.lng),
          destination: new google.maps.LatLng(destination.lat, destination.lng),
          waypoints,
          optimizeWaypoints: false,
          travelMode: google.maps.TravelMode.DRIVING,
        });
        const totals = directionsTotals(result);
        r.totalDistanceKm = totals?.distanceKm;
        r.drivingMinutes = totals?.drivingMinutes;
        directionsRenderer.setDirections(result);
        r.directionsRenderers = [directionsRenderer];
      } else {
        const fullPath = [origin, ...prunedStops, destination];
        const polyline = await drawChainedDirectionsAsPolyline(map, fullPath, undefined, totals => {
          r.totalDistanceKm = totals?.distanceKm; r.drivingMinutes = totals?.drivingMinutes;
        });
        polyline.setOptions({ strokeColor: color, strokeOpacity: 0.95, strokeWeight: 5 });
        r.polyline = polyline;
      }
    };

    useImperativeHandle(ref, () => ({
      clearRoutes: clearAllPolygons,
      assignSelection,
      addOrderToRoute: async (orderId: string, routeIndex?: number) => {
        const order = ordersRef.current.find(item => String(item.orderId) === orderId);
        if (!order || order.isAssigned || !Number.isFinite(order.lat) || !Number.isFinite(order.lng) || selectionWorkingRef.current ||
            polygonRoutesRef.current.some(route => route.ordersInRoute.some(item => String(item.orderId) === orderId)))
          throw new Error("This order cannot be added to the route.");
        if (routeIndex === undefined && pendingPolygonRef.current) {
          manuallyIncluded.current.add(orderId); publishSelection(); await assignSelection(); return;
        }
        const route = routeIndex === undefined ? undefined : polygonRoutesRef.current[routeIndex];
        const assignment = routeSetupRef.current.routeAssignments.find(item => item.driver.driverId === route?.driverId);
        if (!route || !assignment) throw new Error("Choose an existing route.");
        const generation = routeGenerationRef.current;
        const current = () => generation === routeGenerationRef.current && polygonRoutesRef.current.includes(route) &&
          ordersRef.current.some(item => String(item.orderId) === orderId && !item.isAssigned && item.lat === order.lat && item.lng === order.lng);
        selectionWorkingRef.current = true; onSelectionBusyChange?.(true);
        try {
          await processPolygonSelection([...route.ordersInRoute, order], route.polygon, current, { route, assignments: [{ ...assignment,
            vehicleNo: route.vehicleNo, driver: { ...assignment.driver, startLocation: `${route.start.lat},${route.start.lng}`, endLocation: `${route.end.lat},${route.end.lng}` } }] });
        } finally { selectionWorkingRef.current = false; onSelectionBusyChange?.(false); }
      },
      reverseDriverRoute: async (driverId: number) => {
        const r = polygonRoutesRef.current.find((x) => x.driverId === driverId);
        if (!r) return;

        await editAppliedRoute(r);
      },
    }));

    return (
      <>
        <Toast ref={toastRef} />
        {drawingManager && (
          <BigDrawingControls
            map={map}
            drawingManager={drawingManager}
            size={drawingControlHost ? 38 : 60}
            host={drawingControlHost}
            position={google.maps.ControlPosition.TOP_CENTER}
            onClearAll={clearAllPolygons}
            drawingEnabled={drawingEnabled && !selectionWorkingRef.current}
          />
        )}
      </>
    );
  }
);

export default PolygonManager;
